import { FormEvent, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { checkWhitelist } from '../services/betaAccess';
import BetaAccessForm from '../components/BetaAccessForm';
import { IconMark, IconGoogle } from '../components/Icons';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, options: any) => void;
          prompt: () => void;
        };
        oauth2: {
          initTokenClient: (config: any) => any;
        };
      };
    };
  }
}

export default function Login() {
  const { login, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBetaForm, setShowBetaForm] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const whitelistCheck = await checkWhitelist(email);

      if (!whitelistCheck.isWhitelisted) {
        if (whitelistCheck.hasPendingRequest) {
          setError(`Your beta access request is ${whitelistCheck.requestStatus}. We'll notify you once approved.`);
        } else {
          setError('Your email is not whitelisted. Please sign up and request beta access.');
        }
        setIsSubmitting(false);
        return;
      }

      await login(email, password);

      const pendingProject = localStorage.getItem('pendingProject');
      navigate(pendingProject ? '/project-setup' : '/profile');
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Your email is not whitelisted. Please request beta access.');
      } else {
        setError(t('auth:login.failed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = useCallback(
    async (response: any) => {
      setIsSubmitting(true);
      setError('');
      try {
        await googleLogin(response.credential);
        const pendingProject = localStorage.getItem('pendingProject');
        navigate(pendingProject ? '/project-setup' : '/profile');
      } catch (error: any) {
        if (error.response?.status === 403) {
          setShowBetaForm(true);
        } else {
          setError(t('auth:login.googleFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [googleLogin, navigate, t],
  );

  const handleGoogleLoginRef = useRef(handleGoogleLogin);
  useEffect(() => {
    handleGoogleLoginRef.current = handleGoogleLogin;
  }, [handleGoogleLogin]);

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
        callback: (response: any) => handleGoogleLoginRef.current(response),
      });

      const hiddenContainer = document.createElement('div');
      hiddenContainer.id = 'hidden-google-button';
      hiddenContainer.style.display = 'none';
      document.body.appendChild(hiddenContainer);

      window.google.accounts.id.renderButton(hiddenContainer, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
      });

      return () => {
        hiddenContainer.remove();
      };
    }
  }, []);

  const handleGoogleButtonClick = () => {
    const hiddenButton = document.querySelector('#hidden-google-button div[role="button"]') as HTMLElement;
    if (hiddenButton) {
      hiddenButton.click();
    } else if (window.google) {
      window.google.accounts.id.prompt();
    }
  };

  if (showBetaForm) {
    return (
      <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 560 }}>
          <BetaAccessForm
            initialEmail={email}
            onSuccess={() => {
              setShowBetaForm(false);
              setError('');
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: isMobile ? 'auto' : '100dvh', minHeight: '100dvh', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', background: 'var(--bg)' }}>
      {/* Left: form */}
      <div
        style={{
          padding: isMobile ? '28px 20px' : '40px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: isMobile ? 28 : 0,
          borderRight: isMobile ? 'none' : '1px solid var(--line)',
        }}
      >
        <Link to="/" className="brand" style={{ fontSize: 18 }}>
          <IconMark size={22} />
          {t('common:app.name', 'Entire')}
        </Link>

        <div>
          <div className="eyebrow" style={{ marginBottom: 18 }}>
            {t('auth:login.eyebrow', 'Sign in')}
          </div>
          <h2 className="display tight" style={{ fontSize: 40, lineHeight: 1.05, margin: '0 0 14px' }}>
            {t('auth:login.heading', 'Welcome back.')}
          </h2>
          <p className="muted-2" style={{ fontSize: 15, margin: '0 0 32px', maxWidth: 380, lineHeight: 1.5 }}>
            {t('auth:login.subtitle', 'Continue with the account that owns your projects.')}
          </p>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
            {error && (
              <div
                style={{
                  padding: '10px 14px',
                  border: '1px solid var(--danger-line)',
                  background: 'var(--danger-wash)',
                  color: 'var(--danger)',
                  borderRadius: 10,
                  fontSize: 13,
                  marginBottom: 4,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleButtonClick}
              disabled={isSubmitting}
              className="btn lg"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <IconGoogle />
              {t('auth:login.googleButton', 'Continue with Google')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0' }}>
              <div className="hairline" style={{ flex: 1 }} />
              <span className="eyebrow">{t('auth:login.orText', 'or')}</span>
              <div className="hairline" style={{ flex: 1 }} />
            </div>

            <div className="field">
              <input
                id="email"
                type="email"
                required
                className="input"
                placeholder={t('auth:login.emailPlaceholder', 'you@studio.com')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="field">
              <input
                id="password"
                type="password"
                required
                className="input"
                placeholder={t('auth:login.passwordPlaceholder', 'Password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Link
                to="/forgot-password"
                className="nav-link"
                style={{ height: 'auto', padding: 0, alignSelf: 'flex-end', color: 'var(--text-5)', fontSize: 12 }}
              >
                {t('auth:login.forgotPassword', 'Forgot password?')}
              </Link>
            </div>

            <button className="btn primary lg" type="submit" disabled={isSubmitting} style={{ width: '100%', justifyContent: 'center' }}>
              {isSubmitting ? t('auth:login.signingIn', 'Signing in…') : t('auth:login.signInButton', 'Sign in')}
            </button>
          </form>

          <p className="muted" style={{ fontSize: 12, marginTop: 24, maxWidth: 380 }}>
            {t('auth:login.terms', 'By continuing you accept the Terms and Privacy.')}
          </p>
        </div>

        <div />
      </div>

      {/* Right: editorial side */}
      <div
        style={{
          padding: isMobile ? '28px 20px' : '40px 56px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: isMobile ? 20 : 0,
          background: 'var(--bg-2)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link to="/signin" className="nav-link">
            {t('auth:login.newHere', 'New here? Create account →')}
          </Link>
        </div>

        <div>
          <div className="eyebrow" style={{ marginBottom: 14, color: 'var(--accent)' }}>
            {t('auth:login.buildLog', 'Build log · Apr 26')}
          </div>
          <blockquote
            style={{
              margin: 0,
              padding: 0,
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              fontSize: 30,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: 'var(--text)',
            }}
          >
            {t(
              'auth:login.testimonial',
              '“I sketched a Flappy Bird at the airport. By the gate it was playable. By landing it had a leaderboard.”',
            )}
          </blockquote>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, color: 'var(--text-5)', fontSize: 13 }}>
            <span style={{ width: 28, height: 28, borderRadius: 999, background: 'var(--surface-4)', display: 'inline-block' }} />
            <span>
              Rae Okafor &nbsp;<span className="muted">- solo dev, "Espresso Republic"</span>
            </span>
          </div>
        </div>

        <div />
      </div>
    </div>
  );
}
